import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Info, Share2, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getPhone } from "../lib/session";

/**
 * Classic Chocolate wallet card + Add-to-Apple/Google-Wallet flow.
 * Fully server-driven — pulls fresh data on mount.
 */
const Wallet = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const phone = getPhone();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appleBusy, setAppleBusy] = useState(false);
    const [googleBusy, setGoogleBusy] = useState(false);

    useEffect(() => {
        if (!phone) {
            nav(`/${slug}/rewards`);
            return;
        }
        // Phase A: universal wallet is the new source of truth. Any customer
        // with a phone lands on /wallet instead of the per-café view. Old
        // /:slug/wallet links (Instagram bios, printed QRs) keep working
        // because they hit this route first and redirect gracefully.
        if (new URLSearchParams(window.location.search).get("legacy") !== "1") {
            nav("/wallet", { replace: true });
            return;
        }
        api.get(`/wallet/${phone}`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Couldn't load your card"))
            .finally(() => setLoading(false));
    }, [slug, phone, nav]);

    const memberSinceYear = data?.customer?.member_since
        ? new Date(data.customer.member_since).getFullYear()
        : new Date().getFullYear();

    const qrValue = (() => {
        if (!data) return "";
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        return `${origin}${data.order_url}?ref=wallet`;
    })();

    const addAppleWallet = async () => {
        setAppleBusy(true);
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/t/${slug}/wallet/${phone}/apple.pkpass`);
            if (r.status === 501) {
                toast("Apple Wallet coming soon", {
                    description: "We're getting our pass issuer certificate from Apple. Meanwhile, your in-app card works perfectly at the counter.",
                });
                return;
            }
            if (!r.ok) throw new Error("Failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "coffeein7.pkpass";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Couldn't add to Apple Wallet");
        } finally {
            setAppleBusy(false);
        }
    };

    const addGoogleWallet = async () => {
        setGoogleBusy(true);
        try {
            const r = await api.post(`/wallet/${phone}/google/save-url`);
            if (r.data?.url) window.location.assign(r.data.url);
        } catch (e) {
            if (e?.response?.status === 501) {
                toast("Google Wallet coming soon", {
                    description: "Awaiting Google Wallet issuer approval. Your in-app card is ready to go.",
                });
            } else {
                toast.error("Couldn't save to Google Wallet");
            }
        } finally {
            setGoogleBusy(false);
        }
    };

    const shareCard = async () => {
        const shareData = {
            title: `${data.tenant.name} · Coffeein7`,
            text: `${data.beans_balance} Beans · ${data.stamps}/${data.stamps_target} loyalty stamps at ${data.tenant.name}`,
            url: qrValue,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareData.url);
                toast.success("Café link copied to clipboard");
            }
        } catch {
            /* user cancelled */
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#FF4F18]" />
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F9F6F0] to-[#EFE8DA] text-[#2C1A12]">
            <header className="border-b border-[#E8DCCB]/60">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        data-testid="wallet-back-btn"
                        onClick={() => nav(`/${slug}`)}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12]"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back
                    </button>
                    <div className="font-display font-black text-xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <button
                        data-testid="wallet-share-btn"
                        onClick={shareCard}
                        className="h-9 w-9 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center hover:border-[#FF4F18] transition-colors"
                        aria-label="Share"
                    >
                        <Share2 className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-8">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                    Your membership card
                </div>
                <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl leading-tight">
                    {data.tenant.name}
                </h1>
                <p className="mt-2 text-sm text-[#8C7A72] max-w-md">
                    Show this at the counter — or drop it into Apple / Google Wallet so it's always one tap away.
                </p>

                {/* CLASSIC CHOCOLATE CARD */}
                <div className="mt-8 flex justify-center perspective-[1200px]">
                    <motion.div
                        initial={{ rotateX: -10, opacity: 0, y: 30 }}
                        animate={{ rotateX: 0, opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="wallet-card w-full max-w-[420px] aspect-[1.586/1] rounded-[22px] relative overflow-hidden text-white p-5 sm:p-6"
                        style={{
                            background:
                                "radial-gradient(120% 100% at 100% 0%, rgba(255,196,120,0.24), transparent 55%), linear-gradient(160deg, #3B2418 0%, #2C1A12 55%, #120904 100%)",
                            boxShadow:
                                "0 30px 70px -22px rgba(44,26,18,0.6), inset 0 0 0 1px rgba(232,197,138,0.20)",
                        }}
                        data-testid="wallet-card"
                    >
                        {/* Grain overlay */}
                        <div
                            className="absolute inset-0 pointer-events-none opacity-40"
                            style={{
                                backgroundImage:
                                    "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
                                backgroundSize: "3px 3px",
                            }}
                        />
                        {/* Soft embossed corner glow */}
                        <div
                            className="absolute -right-14 -top-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
                            style={{ background: "radial-gradient(circle, #E8C58A 0%, transparent 65%)" }}
                        />

                        {/* Header */}
                        <div className="relative flex items-start justify-between">
                            <div>
                                <div className="text-[9px] uppercase tracking-[0.35em] font-bold text-[#E8C58A]">
                                    Est. {memberSinceYear}
                                </div>
                                <div className="mt-1 leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 900, fontSize: "26px" }} data-testid="wallet-card-cafe">
                                    {data.tenant.name}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[9px] uppercase tracking-[0.3em] font-bold text-[#E8C58A]">
                                    Member
                                </div>
                                <div className="mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: "14px" }} data-testid="wallet-card-name">
                                    {data.customer.name}
                                </div>
                            </div>
                        </div>

                        {/* Gold hairline */}
                        <div className="relative mt-3 border-t" style={{ borderColor: "rgba(232,197,138,0.35)" }} />

                        {/* Loyalty row */}
                        <div className="relative mt-3">
                            <div className="text-[8px] uppercase tracking-[0.3em] font-bold text-[#E8C58A]">
                                Loyalty · every 10th on us
                            </div>
                            <div className="mt-1.5 flex gap-1.5" data-testid="wallet-card-stamps">
                                {Array.from({ length: data.stamps_target }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="rounded-full"
                                        style={{
                                            width: 20,
                                            height: 20,
                                            background: i < data.stamps ? "#E8C58A" : "transparent",
                                            border: i < data.stamps ? "none" : "1.5px dashed rgba(232,197,138,0.35)",
                                            boxShadow: i < data.stamps ? "0 2px 6px rgba(232,197,138,0.3)" : "none",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Bottom row */}
                        <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6 flex items-end justify-between gap-4">
                            <div>
                                <div className="text-[8px] uppercase tracking-[0.3em] font-bold text-[#E8C58A]">
                                    Beans
                                </div>
                                <div
                                    className="leading-none mt-0.5"
                                    style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 900, fontSize: "32px" }}
                                    data-testid="wallet-card-beans"
                                >
                                    {data.beans_balance.toLocaleString()}
                                </div>
                                <div className="mt-1 text-[9px] font-mono text-white/50">
                                    ···{data.customer.phone_last4}
                                </div>
                            </div>

                            {/* Real QR */}
                            <div className="bg-white rounded-lg p-1.5 shrink-0" data-testid="wallet-card-qr">
                                <QRCodeSVG
                                    value={qrValue}
                                    size={72}
                                    level="M"
                                    marginSize={0}
                                    fgColor="#2C1A12"
                                    bgColor="#ffffff"
                                />
                            </div>
                        </div>

                        {/* Free-coffee ribbon */}
                        {data.free_coffee_available && (
                            <div className="absolute top-3 right-3 rounded-full px-2.5 py-1 text-[8px] uppercase tracking-[0.25em] font-black text-[#2C1A12]" style={{ background: "#E8C58A" }}>
                                ✦ Free coffee ready
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Add-to-wallet buttons */}
                <div className="mt-8 max-w-md mx-auto space-y-3" data-testid="wallet-add-buttons">
                    <button
                        data-testid="wallet-add-apple"
                        onClick={addAppleWallet}
                        disabled={appleBusy}
                        className="w-full py-3.5 rounded-2xl bg-black text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.99] hover:bg-[#111] transition-colors"
                    >
                        {appleBusy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <AppleGlyph />
                        )}
                        <span>Add to Apple Wallet</span>
                    </button>
                    <button
                        data-testid="wallet-add-google"
                        onClick={addGoogleWallet}
                        disabled={googleBusy}
                        className="w-full py-3.5 rounded-2xl bg-white text-[#2C1A12] font-semibold border-2 border-[#2C1A12] flex items-center justify-center gap-2 active:scale-[0.99] hover:bg-[#F5F1E8] transition-colors"
                    >
                        {googleBusy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <GoogleGlyph />
                        )}
                        <span>Save to Google Wallet</span>
                    </button>
                </div>

                {/* Helper */}
                <div className="mt-6 rounded-2xl bg-white border border-[#E8DCCB] p-4 flex items-start gap-3 max-w-md mx-auto">
                    <Info className="h-4 w-4 text-[#FF4F18] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="text-xs text-[#8C7A72] leading-relaxed">
                        Baristas scan the QR to pull up your account, apply loyalty, or handle a redemption.
                        The card refreshes automatically when your Beans or stamps change.
                    </div>
                </div>
            </div>
        </div>
    );
};

const AppleGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);

const GoogleGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
        <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.4 12 2.4 6.8 2.4 2.6 6.6 2.6 12S6.8 21.6 12 21.6c6.9 0 9.5-4.8 9.5-7.3 0-.5 0-.9-.1-1.3L12 10.2z" />
        <path fill="#34A853" d="M3.9 7.4l3.2 2.3C8 8 9.9 6.8 12 6.8c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.4 12 2.4 8 2.4 4.6 4.6 3.9 7.4z" />
        <path fill="#FBBC05" d="M12 21.6c2.6 0 4.8-.9 6.3-2.3l-3-2.4c-.9.6-2 1-3.3 1-2.6 0-4.8-1.7-5.6-4.1L3.4 16c1.4 3.4 4.8 5.6 8.6 5.6z" />
        <path fill="#4285F4" d="M21.5 12.3c0-.5 0-.9-.1-1.3H12v3.9h5.5c-.3 1.5-1.3 2.7-2.6 3.6l3 2.4c1.8-1.7 3.6-4.2 3.6-8.6z" />
    </svg>
);

export default Wallet;
